import { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { List as VirtualList } from "react-window";
import {
  Plus,
  Search as SearchIcon,
  Trash2,
  Upload,
  ChevronRight,
  Archive,
  CheckSquare,
  Square,
  Loader2,
} from "lucide-react";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import { clientsApi } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

export default function ClientList() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [clients, setClients] = useState([]);
  const [archived, setArchived] = useState([]);
  const [tab, setTab] = useState("active"); // active | archived
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(new Set());
  const [bulkLoading, setBulkLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [list, arch] = await Promise.all([
        clientsApi.list({ sort_by: "nom" }),
        user?.role === "admin" ? clientsApi.archived() : Promise.resolve([]),
      ]);
      setClients(list);
      setArchived(arch);
    } catch (err) {
      toast.error("Impossible de charger la liste");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const visible = useMemo(() => {
    const src = tab === "active" ? clients : archived;
    if (!search.trim()) return src;
    const q = search.trim().toLowerCase();
    return src.filter((c) => {
      return [c.nom, c.prenom, c.numero_client, c.email, c.telephone, c.ville]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(q));
    });
  }, [tab, clients, archived, search]);

  const toggle = (id) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelected(next);
  };

  const toggleAll = () => {
    if (selected.size === visible.length) setSelected(new Set());
    else setSelected(new Set(visible.map((c) => c.id)));
  };

  const handleBulkArchive = async () => {    if (selected.size === 0) return;
    if (!window.confirm(`Archiver ${selected.size} client(s) ?`)) return;
    setBulkLoading(true);
    try {
      await clientsApi.bulkDelete(Array.from(selected));
      toast.success(`${selected.size} client(s) archivé(s)`);
      setSelected(new Set());
      load();
    } catch (err) {
      toast.error("Erreur lors de l'archivage");
    } finally {
      setBulkLoading(false);
    }
  };

  const handleBulkDeletePermanent = async () => {
    if (selected.size === 0) return;
    if (
      !window.confirm(
        `Supprimer DÉFINITIVEMENT ${selected.size} client(s) ? Cette action est irréversible.`
      )
    )
      return;
    setBulkLoading(true);
    try {
      const r = await clientsApi.bulkDeletePermanent(Array.from(selected));
      toast.success(`${r.deleted} client(s) supprimé(s) définitivement`);
      setSelected(new Set());
      load();
    } catch (err) {
      toast.error("Erreur lors de la suppression");
    } finally {
      setBulkLoading(false);
    }
  };

  const handleDeletePermanent = async (id, name) => {
    if (
      !window.confirm(
        `Supprimer DÉFINITIVEMENT ${name || "ce client"} ? Cette action est irréversible.`
      )
    )
      return;
    try {
      await clientsApi.removePermanent(id);
      toast.success("Client supprimé définitivement");
      load();
    } catch (err) {
      toast.error("Erreur");
    }
  };

  const handleRestore = async (id) => {
    try {
      await clientsApi.restore(id);
      toast.success("Client restauré");
      load();
    } catch (err) {
      toast.error("Erreur restauration");
    }
  };

  // Stable rowProps for the virtualised list. Re-creating this inline on
  // every render would force react-window to recompute every row.
  const listRowProps = useMemo(
    () => ({
      items: visible,
      archivedView: tab === "archived",
      isAdmin: user?.role === "admin",
      selectedSet: selected,
      onToggle: toggle,
      onClick: (id) => navigate(`/clients/${id}`),
      onRestore: handleRestore,
      onDeletePermanent: (id, label) => handleDeletePermanent(id, label),
    }),
    // `toggle`, `handleRestore`, `handleDeletePermanent` are stable enough
    // for this scope — they reference state only through closures invoked
    // at click-time. We intentionally exclude them to avoid invalidating
    // the memo every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [visible, tab, user?.role, selected, navigate],
  );

  return (
    <div data-testid="client-list-page">
      <PageHeader
        title="Clients"
        subtitle={`${clients.length} actif(s)${user?.role === "admin" ? ` · ${archived.length} archivé(s)` : ""}`}
        right={
          <div className="hidden md:flex items-center gap-2">
            <button
              data-testid="client-import-btn"
              onClick={() => navigate("/clients/import")}
              className="px-3 py-2 rounded-xl bg-[var(--vidoc-bg-secondary)] hover:bg-[var(--vidoc-bg-tertiary)] flex items-center gap-2 touch-target text-sm font-medium"
            >
              <Upload className="w-4 h-4" />
              Importer
            </button>
            <button
              data-testid="client-add-btn"
              onClick={() => navigate("/clients/add")}
              className="px-3 py-2 rounded-xl bg-[var(--vidoc-primary)] text-white hover:bg-[var(--vidoc-primary-dark)] flex items-center gap-2 touch-target text-sm font-medium"
            >
              <Plus className="w-4 h-4" />
              Nouveau
            </button>
          </div>
        }
      />

      <div className="px-4 md:px-8 py-4 space-y-3 md:space-y-4">
        {/* Mobile action buttons */}
        <div className="md:hidden grid grid-cols-2 gap-2">
          <button
            data-testid="client-add-btn-mobile"
            onClick={() => navigate("/clients/add")}
            className="px-3 py-2.5 rounded-xl bg-[var(--vidoc-primary)] text-white flex items-center justify-center gap-2 touch-target text-sm font-medium"
          >
            <Plus className="w-4 h-4" /> Nouveau
          </button>
          <button
            data-testid="client-import-btn-mobile"
            onClick={() => navigate("/clients/import")}
            className="px-3 py-2.5 rounded-xl bg-[var(--vidoc-bg-secondary)] flex items-center justify-center gap-2 touch-target text-sm font-medium"
          >
            <Upload className="w-4 h-4" /> Importer
          </button>
        </div>

        {/* Search */}
        <div className="relative">
          <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[var(--vidoc-text-tertiary)]" />
          <input
            data-testid="client-search-bar"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Rechercher par nom, n° client, email..."
            className="w-full pl-10 pr-3 py-3 rounded-xl bg-[var(--vidoc-bg-secondary)] border border-transparent focus:border-[var(--vidoc-primary)] focus:bg-white outline-none touch-target"
          />
        </div>

        {/* Tabs */}
        {user?.role === "admin" && (
          <div className="flex gap-1 bg-[var(--vidoc-bg-secondary)] p-1 rounded-xl w-fit">
            <TabBtn active={tab === "active"} onClick={() => setTab("active")} testid="tab-active">
              Actifs ({clients.length})
            </TabBtn>
            <TabBtn active={tab === "archived"} onClick={() => setTab("archived")} testid="tab-archived">
              <Archive className="w-3.5 h-3.5 inline mr-1" />
              Archivés ({archived.length})
            </TabBtn>
          </div>
        )}

        {/* Bulk actions */}
        {tab === "active" && selected.size > 0 && (
          <div className="flex items-center justify-between gap-2 bg-[var(--vidoc-primary)] text-white px-4 py-2.5 rounded-xl animate-slideUp">
            <span className="text-sm font-medium">{selected.size} sélectionné(s)</span>
            <div className="flex items-center gap-1">
              <button
                data-testid="bulk-archive-btn"
                onClick={handleBulkArchive}
                disabled={bulkLoading}
                className="text-sm font-semibold flex items-center gap-1.5 hover:bg-white/10 px-3 py-1 rounded-lg"
              >
                {bulkLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Archive className="w-4 h-4" />}
                Archiver
              </button>
              {user?.role === "admin" && (
                <button
                  data-testid="bulk-delete-permanent-btn"
                  onClick={handleBulkDeletePermanent}
                  disabled={bulkLoading}
                  className="text-sm font-semibold flex items-center gap-1.5 hover:bg-white/10 px-3 py-1 rounded-lg border border-white/30"
                  title="Supprimer définitivement (admin)"
                >
                  <Trash2 className="w-4 h-4" />
                  <span className="hidden sm:inline">Supprimer</span>
                </button>
              )}
            </div>
          </div>
        )}

        {/* List */}
        {loading ? (
          <div className="text-center py-12 text-[var(--vidoc-text-secondary)]">
            Chargement...
          </div>
        ) : visible.length === 0 ? (
          <div className="text-center py-12 text-[var(--vidoc-text-secondary)]">
            Aucun client trouvé
          </div>
        ) : (
          <div className="bg-white rounded-xl border border-[var(--vidoc-border)] divide-y divide-[var(--vidoc-border)] overflow-hidden">
            {tab === "active" && (
              <button
                onClick={toggleAll}
                className="w-full text-xs px-4 py-2 text-left text-[var(--vidoc-text-secondary)] hover:bg-[var(--vidoc-bg-secondary)] flex items-center gap-2"
              >
                {selected.size === visible.length ? (
                  <CheckSquare className="w-4 h-4" />
                ) : (
                  <Square className="w-4 h-4" />
                )}
                Tout sélectionner
              </button>
            )}
            {/* Virtualised list: renders only the rows on screen so 5000+
                clients scroll smoothly on iPad/iPhone. ~64px per row.
                IMPORTANT: react-window v2 requires an explicit pixel height
                on the List itself — otherwise it expands to fit content. */}
            <VirtualList
              rowCount={visible.length}
              rowHeight={64}
              defaultHeight={Math.min(visible.length * 64, 640)}
              style={{ height: Math.min(visible.length * 64, 640) }}
              overscanCount={6}
              rowComponent={ClientRowVirtual}
              rowProps={listRowProps}
              data-testid="clients-virtual-list"
            />
          </div>
        )}
      </div>
    </div>
  );
}

function TabBtn({ active, onClick, children, testid }) {
  return (
    <button
      data-testid={testid}
      onClick={onClick}
      className={`px-3 py-1.5 rounded-lg text-sm font-medium ${
        active ? "bg-white shadow-sm" : "text-[var(--vidoc-text-secondary)]"
      }`}
    >
      {children}
    </button>
  );
}

function ClientRow({
  client,
  archivedView,
  isAdmin,
  selected,
  onToggle,
  onClick,
  onRestore,
  onDeletePermanent,
}) {
  return (
    <div
      data-testid={`client-row-${client.id}`}
      className="flex items-center gap-3 px-4 py-3 hover:bg-[var(--vidoc-bg-secondary)]"
    >
      {!archivedView && (
        <button
          onClick={onToggle}
          data-testid={`client-select-${client.id}`}
          className="p-1 -ml-1"
        >
          {selected ? (
            <CheckSquare className="w-5 h-5 text-[var(--vidoc-primary)]" />
          ) : (
            <Square className="w-5 h-5 text-[var(--vidoc-text-tertiary)]" />
          )}
        </button>
      )}
      <button onClick={onClick} className="flex-1 text-left min-w-0">
        <div className="font-medium truncate">
          {client.nom}
          {client.prenom ? ` ${client.prenom}` : ""}
          {client.numero_client && (
            <span className="ml-2 text-xs text-[var(--vidoc-text-secondary)] font-normal">
              · N° {client.numero_client}
            </span>
          )}
        </div>
        <div className="text-xs text-[var(--vidoc-text-secondary)] truncate">
          {[client.email, client.telephone, client.ville].filter(Boolean).join(" · ")}
        </div>
      </button>
      {archivedView ? (
        <div className="flex items-center gap-1 shrink-0">
          <button
            data-testid={`restore-${client.id}`}
            onClick={onRestore}
            className="text-sm text-[var(--vidoc-primary)] font-medium px-3 py-1.5 rounded-lg hover:bg-[var(--vidoc-bg-tertiary)]"
          >
            Restaurer
          </button>
          {isAdmin && (
            <button
              data-testid={`delete-permanent-${client.id}`}
              onClick={onDeletePermanent}
              className="p-1.5 rounded-lg text-[var(--vidoc-danger)] hover:bg-red-50"
              title="Supprimer définitivement"
              aria-label="Supprimer définitivement"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          )}
        </div>
      ) : (
        <ChevronRight className="w-4 h-4 text-[var(--vidoc-text-tertiary)] shrink-0" />
      )}
    </div>
  );
}

// react-window v2 row wrapper. Receives the spread `style` (top/height) that
// positions the row inside the virtualised viewport; delegates rendering to
// the original ClientRow component used by tests.
function ClientRowVirtual({
  index,
  style,
  items,
  archivedView,
  isAdmin,
  selectedSet,
  onToggle,
  onClick,
  onRestore,
  onDeletePermanent,
}) {
  const client = items[index];
  if (!client) return null;
  return (
    <div style={style} className="border-b border-[var(--vidoc-border)] last:border-b-0">
      <ClientRow
        client={client}
        archivedView={archivedView}
        isAdmin={isAdmin}
        selected={selectedSet.has(client.id)}
        onToggle={() => onToggle(client.id)}
        onClick={() => onClick(client.id)}
        onRestore={() => onRestore(client.id)}
        onDeletePermanent={() =>
          onDeletePermanent(
            client.id,
            `${client.nom}${client.prenom ? " " + client.prenom : ""}`,
          )
        }
      />
    </div>
  );
}
